import Stack from '@mui/material/Stack';

import { ensureDefined, invariant } from '@e-socna/utils';
import { ThirdPartyTypeEnum, getFragmentData } from '@e-socna/data-layer';

import { useThirdPartiesQuery } from '../../../../components/ThirdParties/hooks/useThirdPartiesQuery.hook';
import { NoData } from '../../../../components/Illustrations/NoData';
import { Loading } from '../../../../components/Loading';
import { ThirdPartiesCmp_Person_Fragment } from '../../../../components/ThirdParties/fragments/person.fragment';

import type { ThirdPartyForm_Organization } from '../../fragments/organization.fragment';

import { MembershipDetails } from '../MembershipDetails';

interface Props {
  readonly activeMemberships: ThirdPartyForm_Organization['activeMemberships'];
  readonly isInternal: boolean;
}

export const MembershipsDetails = ({
  activeMemberships,
  isInternal,
}: Readonly<Props>) => {
  const { data, isSuccess } = useThirdPartiesQuery({
    isInternal,
    kind: ThirdPartyTypeEnum.Person,
  });

  if (!activeMemberships.length) {
    return <NoData />;
  }

  if (!isSuccess) {
    return <Loading />;
  }

  invariant(
    !!data && 'findSomePeople' in data,
    'findSomePeople must be defined',
  );

  const people = getFragmentData(
    ThirdPartiesCmp_Person_Fragment,
    data.findSomePeople,
  );

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {activeMemberships.map((activeMembership, index) => {
        const { person } = ensureDefined({
          person: people.find(
            (person) => person._id === activeMembership.personId,
          ),
        });

        return (
          <MembershipDetails
            index={index}
            isInternal={isInternal}
            key={activeMembership.personId}
            kind={ThirdPartyTypeEnum.Organization}
            organizationMembership={activeMembership}
            person={person}
          />
        );
      })}
    </Stack>
  );
};
